import * as THREE from 'three';
import type { V2 } from '../../core/math';
import type { Decor } from '../decor';
import { districtAt } from '../lowBorough/data';
import type { BuildingLot } from '../types';
import { PAVE_Y, WATER_Y } from './ground';

/**
 * Breakable street lamps: the sodium lantern heads and their light pools / wet-road streaks / water
 * reflections are two InstancedMeshes (heads, pools), so a single lamp can flicker and die when a car
 * takes the post out. Posts themselves stay in the merged static geometry.
 */
export interface LampInfo {
  pos: V2;
  heading: number;
  type: 'lamp' | 'bridgeLamp';
}

export interface LampsView {
  readonly lamps: readonly LampInfo[];
  /** Index of the lamp standing at `p` (within 0.5 m), or -1. */
  lampAt(p: V2): number;
  /** Knock a lamp out (flicker, then dark). Returns false if already broken. */
  breakLamp(index: number): boolean;
  /** Mood multiplier for heads and pools. */
  setLevel(level: number): void;
  /** 0 = dry road (the wet-road streak under each lamp all but vanishes), 1 = soaked. */
  setWetness(w: number): void;
  /** Quality tier: LOW drops the shop-window reflections (the lamp streaks stay on every tier). */
  setTier(tier: 'low' | 'medium' | 'high'): void;
  /** The ripple normal that breaks up the reflections, once the baked file has replaced its placeholder. */
  setRipple(t: THREE.Texture): void;
  update(dt: number): void;
  dispose(): void;
}

/**
 * Lamp kinds by district (docs/POLISH.md, "City pass 3"): each quarter of Low Borough is lit its own
 * way, so you can tell where you are at night by the colour of the street.
 * - The Parade and the main roads: new high-pressure sodium, amber.
 * - Tanner's Row and Mercy Square: the old mercury lanterns, cold blue-green white.
 * - Gullet Basin: the dock board's low-pressure sodium, a deeper orange, strong.
 * - Cinder Yards: the oldest tungsten lanterns, a dim yellow-white that leaves the yards dark.
 * Bridge lamps are sodium.
 */
export type LampKind = 'sodium' | 'mercury' | 'lpSodium' | 'tungsten';
const HEAD: Record<LampKind, THREE.Color> = {
  sodium: new THREE.Color(4.2, 2.3, 0.9),
  mercury: new THREE.Color(2.1, 2.7, 2.8),
  lpSodium: new THREE.Color(4.4, 1.9, 0.45),
  tungsten: new THREE.Color(2.6, 2.0, 1.2),
};
const POOL: Record<LampKind, [number, number, number]> = {
  sodium: [0.46, 0.24, 0.08],
  mercury: [0.15, 0.2, 0.225],
  lpSodium: [0.5, 0.2, 0.04],
  tungsten: [0.26, 0.2, 0.11],
};
/** Halo colour (the lantern's glow seen through damp air), per kind. */
const HALO: Record<LampKind, [number, number, number]> = {
  sodium: [0.55, 0.3, 0.1],
  mercury: [0.2, 0.27, 0.3],
  lpSodium: [0.6, 0.25, 0.05],
  tungsten: [0.3, 0.23, 0.13],
};

export function lampKind(l: LampInfo): LampKind {
  if (l.type !== 'lamp') return 'sodium';
  const d = districtAt(l.pos);
  if (d === 'tanners-row' || d === 'mercy-square') return 'mercury';
  if (d === 'gullet-basin') return 'lpSodium';
  if (d === 'cinder-yards') return 'tungsten';
  return 'sodium';
}

/** Camera-facing glow quad per lantern: one instanced draw, additive, depth-tested against the city. */
const HALO_VERT = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vCol;
  void main() {
    vUv = uv;
    vCol = instanceColor;
    vec3 centre = (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
    float size = length(vec3(instanceMatrix[0].xyz));
    vec3 right = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
    vec3 up = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
    vec3 p = centre + (right * position.x + up * position.y) * size;
    gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
  }`;
const HALO_FRAG = /* glsl */ `
  uniform sampler2D uGlow;
  uniform float uLevel;
  varying vec2 vUv;
  varying vec3 vCol;
  void main() {
    // the glow texture is white with its falloff in the alpha channel
    float a = texture2D(uGlow, vUv).a;
    gl_FragColor = vec4(vCol * a * a * uLevel, 1.0);
  }`;

/**
 * Wet-street reflections (docs/POLISH.md, "Graphics batch 2"). The streaks share the pools' instanced
 * draw; an instance attribute `aRefl` = (kind, source height, frontage normal x, z) turns a pool quad
 * into a reflection: kind 1 = lamp on a wet road, 2 = lamp on the canal / basin, 3 = lit shop glass on
 * a wet road. The vertex shader lays the quad from the source's foot towards the camera through the
 * mirror point (where a flat wet surface shows the source to this eye: D * h / (h + H)), stretched by
 * the wetness; the fragment shader shapes it into a soft column broken into ripple bands by the
 * water's own ripple normal map. Instance scale = (width, 1, base length); a zero matrix (a dead lamp)
 * collapses it. No extra draw, no extra pass, a texture read per streak fragment.
 */
const REFL_VERT_PARS = /* glsl */ `
attribute vec4 aRefl;
uniform float uWet;
uniform float uShopOn;
varying vec4 vRefl;
varying vec4 vReflB;
`;
const REFL_VERT = /* glsl */ `
  vRefl = vec4(0.0);
  vReflB = vec4(0.0);
  if (aRefl.x > 0.5) {
    vec3 anchor = instanceMatrix[3].xyz;
    float wid = length(instanceMatrix[0].xyz);
    float base = length(instanceMatrix[2].xyz);
    vec2 toCam = cameraPosition.xz - anchor.xz;
    float dist = max(length(toCam), 0.01);
    vec2 dir = toCam / dist;
    float camH = max(cameraPosition.y - anchor.y, 0.5);
    float h = aRefl.y;
    bool shop = aRefl.x > 2.5;
    bool water = aRefl.x > 1.5 && !shop;
    // a wet road is a rough mirror: the image stretches towards the eye, longer the wetter it is
    float len = base * (water ? 1.0 : 0.5 + 0.5 * uWet);
    float m = dist * h / (h + camH);
    float s0;
    float s1;
    float gain = 1.0;
    vec2 across;
    if (shop) {
      // a lit shop window is a tall source: its image runs from the foot of the glass (0.4 m) to the
      // fascia, then the wet road drags it on towards the viewer; none if we stand behind the shop
      vec2 n = aRefl.zw;
      float facing = dot(n, dir);
      gain = smoothstep(0.1, 0.45, facing) * uWet * uShopOn;
      s0 = dist * 0.4 / (0.4 + camH);
      s1 = m + len;
      across = vec2(n.y, -n.x);
    } else {
      s0 = max(0.0, m - 0.3 * len);
      s1 = m + 0.7 * len;
      across = vec2(dir.y, -dir.x); // (this handedness keeps the quad facing up)
    }
    float t = position.z + 0.5;
    float along = mix(s0, s1, t);
    vec3 wp = anchor + vec3(across * position.x * wid + dir * along, 0.0).xzy;
    wp.y = anchor.y;
    // w: where the mirror point falls along the streak (for a shop: the frontage width, for its bays)
    vRefl = vec4(t, position.x * 2.0, aRefl.x, shop ? wid : clamp((m - s0) / max(s1 - s0, 0.01), 0.0, 1.0));
    vReflB = vec4(wp.x, wp.z, s1 - s0, gain);
    mvPosition = viewMatrix * vec4(wp, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    // shop streaks with nothing to show (dry, day-lit or LOW) cost no fragments
    if (gain < 0.003) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
  }
`;
const REFL_FRAG_PARS = /* glsl */ `
uniform sampler2D uRipple;
uniform float uReflT;
varying vec4 vRefl;
varying vec4 vReflB;
`;
const REFL_FRAG = /* glsl */ `
  if (vRefl.z > 0.5) {
    float kind = vRefl.z;
    float water = step(1.5, kind) * (1.0 - step(2.5, kind));
    float shop = step(2.5, kind);
    float t = vRefl.x;
    float lenM = vReflB.z;
    // the ripple normal (the canal's own; finer and quicker on a road, where it is rain) wobbles the
    // column sideways and pushes its bands about
    vec2 uvR = vReflB.xy * mix(0.42, 0.16, water) + uReflT * mix(vec2(0.021, -0.017), vec2(0.006, 0.011), water);
    vec2 rip = texture2D(uRipple, uvR).xy * 2.0 - 1.0;
    float wob = rip.x * mix(0.3, 0.75, water) * smoothstep(0.0, 0.6, t);
    float shape;
    if (shop > 0.5) {
      // one soft column per window bay, fading off the ends of the frontage and away from the glass
      float sA = vRefl.y + wob * 0.12;
      float edge = 1.0 - smoothstep(0.72, 1.0, abs(sA));
      float bays = max(1.0, floor(vRefl.w / 1.8 + 0.5));
      float pane = 0.5 + 0.5 * cos((sA * 0.5 + 0.5) * bays * 6.2831853);
      shape = edge * (0.25 + 0.75 * pane * pane) * smoothstep(0.0, 0.12, t) * (1.0 - smoothstep(0.2, 1.0, t));
    } else {
      float across = abs(vRefl.y + wob);
      // a bright thread down the middle inside a soft sheen
      float core = 0.55 * exp(-across * across * 14.0) + 0.45 * exp(-across * across * 3.5);
      // soft ends: fades in from the source's foot and out towards the viewer, brightest about the
      // mirror point
      float ends = smoothstep(0.0, 0.22, t) * (1.0 - smoothstep(mix(0.45, 0.6, water), 1.0, t));
      float dp = (t - vRefl.w) / 0.28;
      shape = core * ends * (0.4 + 0.6 * exp(-dp * dp));
    }
    // broken into bands: two incommensurate ripples along the length (~1 m on a road, tighter on the
    // canal), their edges pushed about by the ripple map; whole near the source, broken further off
    float x = t * lenM * mix(5.0, 8.5, water);
    float bw = sin(x + rip.y * 3.0) * 0.6 + sin(x * 2.37 + 1.7 + rip.x * 2.5) * 0.4;
    float amt = mix(0.55, 0.6, water) * smoothstep(0.05, 0.6, t) - 0.25 * shop;
    float bands = mix(1.0, smoothstep(-0.7, 0.7, bw), amt);
    diffuseColor = vec4(diffuse, opacity * shape * bands * vReflB.w);
  }
`;

/** A lit shopfront whose glass mirrors in a wet road (from the shop-light pools in streetLife). */
function shopReflections(lots: readonly BuildingLot[]): { x: number; z: number; nx: number; nz: number; w: number; c: [number, number, number] }[] {
  const out: { x: number; z: number; nx: number; nz: number; w: number; c: [number, number, number] }[] = [];
  for (const lot of lots) {
    let c: [number, number, number] | null = null;
    switch (lot.style) {
      case 'shopfront':
        c = (lot.name ?? '').toLowerCase().includes('launderette') ? [0.2, 0.28, 0.28] : [0.3, 0.21, 0.1];
        break;
      case 'cafe':
        c = [0.32, 0.22, 0.11];
        break;
      case 'pub':
        c = [0.4, 0.23, 0.08];
        break;
      case 'club':
        c = [0.36, 0.12, 0.24];
        break;
      default:
        break;
    }
    if (!c || lot.districtId === 'backdrop') continue;
    const nx = Math.sin(lot.angle);
    const nz = Math.cos(lot.angle);
    out.push({ x: lot.center.x + nx * lot.halfZ, z: lot.center.z + nz * lot.halfZ, nx, nz, w: Math.max(2, lot.halfX * 2 - 1.2), c });
  }
  return out;
}

export function buildLampsView(scene: THREE.Scene, decor: Decor, glowTex: THREE.Texture, opts: { lots?: readonly BuildingLot[]; ripple?: THREE.Texture } = {}): LampsView {
  const lamps: LampInfo[] = decor.props.filter((p) => p.type === 'lamp' || p.type === 'bridgeLamp').map((p) => ({ pos: { ...p.pos }, heading: p.heading, type: p.type as 'lamp' | 'bridgeLamp' }));
  const headCount = lamps.reduce((n, l) => n + (l.type === 'bridgeLamp' ? 2 : 1), 0);
  const headGeo = new THREE.BoxGeometry(1, 1, 1);
  const headMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
  const heads = new THREE.InstancedMesh(headGeo, headMat, headCount);
  heads.name = 'lamp-heads';
  heads.frustumCulled = false;
  const haloGeo = new THREE.PlaneGeometry(1, 1);
  const haloMat = new THREE.ShaderMaterial({
    uniforms: { uGlow: { value: glowTex }, uLevel: { value: 1 } },
    vertexShader: HALO_VERT,
    fragmentShader: HALO_FRAG,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const halos = new THREE.InstancedMesh(haloGeo, haloMat, headCount);
  halos.name = 'lamp-halos';
  halos.frustumCulled = false;
  halos.renderOrder = 3;

  const poolGeo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
  const poolMat = new THREE.MeshBasicMaterial({ map: glowTex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4 });
  const reflUniforms = { uWet: { value: 1 }, uShopOn: { value: 1 }, uReflT: { value: 0 }, uRipple: { value: opts.ripple ?? null } };
  if (opts.ripple) {
    poolMat.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, reflUniforms);
      shader.vertexShader = shader.vertexShader.replace('#include <common>', `#include <common>\n${REFL_VERT_PARS}`).replace('#include <project_vertex>', `#include <project_vertex>\n${REFL_VERT}`);
      shader.fragmentShader = shader.fragmentShader.replace('#include <common>', `#include <common>\n${REFL_FRAG_PARS}`).replace('#include <map_fragment>', `#include <map_fragment>\n${REFL_FRAG}`);
    };
    poolMat.customProgramCacheKey = () => 'bhlampRefl';
  }
  /** kind: 0 pool, 1 road streak, 2 water streak, 3 shop streak (see REFL_VERT); h = source height. */
  const poolSpecs: { lamp: number; x: number; y: number; z: number; sx: number; sz: number; c: [number, number, number]; streak?: boolean; kind?: number; h?: number; n?: [number, number] }[] = [];
  const headSpecs: { lamp: number; m: THREE.Matrix4; halo: THREE.Matrix4 }[] = [];

  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  const v = new THREE.Vector3();
  const s = new THREE.Vector3();
  const kinds = lamps.map(lampKind);
  const haloAt = (x: number, y: number, z: number, size: number): THREE.Matrix4 => new THREE.Matrix4().compose(v.set(x, y, z), q.identity(), s.set(size, size, size));
  lamps.forEach((l, i) => {
    const fx = Math.sin(l.heading);
    const fz = Math.cos(l.heading);
    q.setFromAxisAngle(up, l.heading);
    if (l.type === 'lamp') {
      m.compose(v.set(l.pos.x + fx * 1.25, PAVE_Y + 5.96, l.pos.z + fz * 1.25), q, s.set(0.3, 0.22, 0.3));
      headSpecs.push({ lamp: i, m: m.clone(), halo: haloAt(l.pos.x + fx * 1.25, PAVE_Y + 5.9, l.pos.z + fz * 1.25, kinds[i] === 'tungsten' ? 2.2 : 3.0) });
    } else {
      for (const sx of [-0.8, 0.8]) {
        // local +X = (cos h, -sin h)
        const hx = l.pos.x + Math.cos(l.heading) * sx;
        const hz = l.pos.z - Math.sin(l.heading) * sx;
        m.compose(v.set(hx, PAVE_Y + 4.35, hz), q, s.set(0.3, 0.3, 0.3));
        headSpecs.push({ lamp: i, m: m.clone(), halo: haloAt(hx, PAVE_Y + 4.3, hz, 2.4) });
      }
    }
    const reach = 1.3;
    const hx = l.pos.x + fx * reach;
    const hz = l.pos.z + fz * reach;
    const y = PAVE_Y + 0.03;
    const kind = kinds[i]!;
    const pc = POOL[kind];
    const dim = kind === 'tungsten' ? 0.8 : 1;
    poolSpecs.push({ lamp: i, x: hx, y, z: hz, sx: 8.8 * dim, sz: 8.8 * dim, c: pc });
    // a soft outer halo so the pool fades into the street instead of ending in a disc
    poolSpecs.push({ lamp: i, x: hx, y: y - 0.002, z: hz, sx: 15 * dim, sz: 15 * dim, c: [pc[0] * 0.28, pc[1] * 0.28, pc[2] * 0.28] });
    const headY = l.type === 'lamp' ? PAVE_Y + 5.96 : PAVE_Y + 4.35;
    if (opts.ripple) {
      // the reflection column, laid towards the camera in the shader (see REFL_VERT)
      poolSpecs.push({ lamp: i, x: hx, y, z: hz, sx: 2.0, sz: 8.5, c: [pc[0] * 0.9, pc[1] * 0.9, pc[2] * 0.9], streak: true, kind: 1, h: headY - y });
    } else poolSpecs.push({ lamp: i, x: hx, y, z: hz + 4.2, sx: 1.8, sz: 9.2, c: [pc[0] * 0.54, pc[1] * 0.54, pc[2] * 0.54], streak: true });
    for (const w of decor.water) {
      const sz = hz + 7;
      if (hx > w.minX && hx < w.maxX && sz > w.minZ + 1 && sz < w.maxZ) {
        const z0 = Math.max(w.minZ + 0.3, hz + 1);
        if (opts.ripple) poolSpecs.push({ lamp: i, x: hx, y: WATER_Y + 0.02, z: hz, sx: 2.4, sz: 14, c: [0.85, 0.46, 0.15], kind: 2, h: headY - WATER_Y });
        else poolSpecs.push({ lamp: i, x: hx, y: WATER_Y + 0.02, z: z0 + 5.5, sx: 1.8, sz: 11, c: [0.55, 0.3, 0.1] });
        break;
      }
    }
  });

  // lit shop glass mirrored in the wet road in front (HIGH and MEDIUM; nothing drawn when dry or by day)
  if (opts.ripple && opts.lots) {
    for (const sh of shopReflections(opts.lots)) poolSpecs.push({ lamp: -1, x: sh.x, y: PAVE_Y + 0.03, z: sh.z, sx: sh.w, sz: 3.2, c: sh.c, kind: 3, h: 3.3, n: [sh.nx, sh.nz] });
  }
  if (opts.ripple) {
    const refl = new Float32Array(poolSpecs.length * 4);
    poolSpecs.forEach((p, k) => {
      refl[k * 4] = p.kind ?? 0;
      refl[k * 4 + 1] = p.h ?? 0;
      refl[k * 4 + 2] = p.n?.[0] ?? 0;
      refl[k * 4 + 3] = p.n?.[1] ?? 0;
    });
    poolGeo.setAttribute('aRefl', new THREE.InstancedBufferAttribute(refl, 4));
  }
  const pools = new THREE.InstancedMesh(poolGeo, poolMat, poolSpecs.length);
  pools.name = 'lamp-pools';
  pools.frustumCulled = false;
  pools.renderOrder = 2;
  const col = new THREE.Color();
  const headsOf: number[][] = lamps.map(() => []);
  const poolsOf: number[][] = lamps.map(() => []);
  const headColour = (lamp: number): THREE.Color => HEAD[kinds[lamp]!];
  const haloColour = (lamp: number): THREE.Color => {
    const h = HALO[kinds[lamp]!];
    return col.setRGB(h[0], h[1], h[2]);
  };
  headSpecs.forEach((h, k) => {
    heads.setMatrixAt(k, h.m);
    heads.setColorAt(k, headColour(h.lamp));
    halos.setMatrixAt(k, h.halo);
    halos.setColorAt(k, haloColour(h.lamp));
    headsOf[h.lamp]!.push(k);
  });
  poolSpecs.forEach((p, k) => {
    m.compose(v.set(p.x, p.y, p.z), q.identity(), s.set(p.sx, 1, p.sz));
    pools.setMatrixAt(k, m);
    pools.setColorAt(k, col.setRGB(p.c[0], p.c[1], p.c[2]));
    if (p.lamp >= 0) poolsOf[p.lamp]!.push(k);
  });
  scene.add(heads, pools, halos);

  // Spatial lookup for collisions.
  const byCell = new Map<string, number[]>();
  const cell = (x: number, z: number): string => `${Math.floor(x / 8)},${Math.floor(z / 8)}`;
  lamps.forEach((l, i) => {
    const k = cell(l.pos.x, l.pos.z);
    const list = byCell.get(k) ?? [];
    list.push(i);
    byCell.set(k, list);
  });

  const broken = new Uint8Array(lamps.length);
  const dying: { lamp: number; t: number }[] = [];
  const zero = new THREE.Matrix4().makeScale(0, 0, 0);
  let level = 1;
  /** Wet-road streak strength (a dry road barely mirrors the lamp). */
  let streakF = 1;
  const poolColour = (p: (typeof poolSpecs)[number], f: number): THREE.Color => {
    const g = p.streak ? f * streakF : f;
    return col.setRGB(p.c[0] * g, p.c[1] * g, p.c[2] * g);
  };

  const setLampColour = (i: number, f: number): void => {
    for (const k of headsOf[i]!) {
      heads.setColorAt(k, col.copy(headColour(i)).multiplyScalar(f));
      halos.setColorAt(k, haloColour(i).multiplyScalar(f));
    }
    for (const k of poolsOf[i]!) {
      pools.setColorAt(k, poolColour(poolSpecs[k]!, f));
    }
    if (heads.instanceColor) heads.instanceColor.needsUpdate = true;
    if (halos.instanceColor) halos.instanceColor.needsUpdate = true;
    if (pools.instanceColor) pools.instanceColor.needsUpdate = true;
  };
  /** Mood level and road wetness decide how much the lanterns glow through the air. */
  let haloLevel = 1;
  let haloWet = 0;
  const applyHalo = (): void => {
    // the glow is the lamp seen through drizzle: faint when dry, full when it is raining
    const f = Math.max(0, haloLevel - 0.3) * (0.45 + 0.55 * haloWet);
    haloMat.uniforms.uLevel!.value = f;
    halos.visible = f > 0.02;
  };

  return {
    lamps,
    lampAt(p) {
      const cx = Math.floor(p.x / 8);
      const cz = Math.floor(p.z / 8);
      for (let dx = -1; dx <= 1; dx++) {
        for (let dz = -1; dz <= 1; dz++) {
          for (const i of byCell.get(`${cx + dx},${cz + dz}`) ?? []) {
            const l = lamps[i]!;
            if (Math.hypot(l.pos.x - p.x, l.pos.z - p.z) < 0.5) return i;
          }
        }
      }
      return -1;
    },
    breakLamp(i) {
      if (i < 0 || i >= lamps.length || broken[i]) return false;
      broken[i] = 1;
      dying.push({ lamp: i, t: 0 });
      return true;
    },
    setLevel(l) {
      level = l;
      headMat.color.setScalar(Math.max(0.3, l));
      poolMat.color.setScalar(l);
      haloLevel = l;
      applyHalo();
    },
    setTier(t) {
      reflUniforms.uShopOn.value = t === 'low' ? 0 : 1;
    },
    setRipple(t) {
      if (opts.ripple) reflUniforms.uRipple.value = t;
    },
    setWetness(w) {
      haloWet = Math.max(0, Math.min(1, w));
      reflUniforms.uWet.value = haloWet;
      applyHalo();
      const f = 0.12 + 0.88 * Math.max(0, Math.min(1, w));
      if (Math.abs(f - streakF) < 0.03 && f > 0.13 && f < 0.99) return;
      if (f === streakF) return;
      streakF = f;
      poolSpecs.forEach((p, k) => {
        if (p.streak && !broken[p.lamp]) pools.setColorAt(k, poolColour(p, 1));
      });
      if (pools.instanceColor) pools.instanceColor.needsUpdate = true;
    },
    update(dt) {
      void level;
      reflUniforms.uReflT.value = (reflUniforms.uReflT.value + dt) % 1000;
      for (let n = dying.length - 1; n >= 0; n--) {
        const d = dying[n]!;
        d.t += dt;
        if (d.t > 0.9) {
          // dead: hide the head glow, its halo and the pools entirely
          for (const k of headsOf[d.lamp]!) {
            heads.setMatrixAt(k, zero);
            halos.setMatrixAt(k, zero);
          }
          for (const k of poolsOf[d.lamp]!) pools.setMatrixAt(k, zero);
          heads.instanceMatrix.needsUpdate = true;
          halos.instanceMatrix.needsUpdate = true;
          pools.instanceMatrix.needsUpdate = true;
          dying.splice(n, 1);
        } else {
          // sputtering sodium tube
          const on = Math.sin(d.t * 47) + Math.sin(d.t * 23.3) > 0.4;
          setLampColour(d.lamp, on ? 1 - d.t * 0.6 : 0.05);
        }
      }
    },
    dispose() {
      scene.remove(heads, pools, halos);
      headGeo.dispose();
      headMat.dispose();
      poolGeo.dispose();
      poolMat.dispose();
      haloGeo.dispose();
      haloMat.dispose();
      heads.dispose();
      pools.dispose();
      halos.dispose();
    },
  };
}
